// Switching Meta's AI agent on for one of your WhatsApp numbers, and telling it
// what it needs to know.
//
// THE SHAPE OF THIS SCREEN IS DECIDED BY THREE FACTS, none of them obvious.
//
// 1. A REFUSED CHECK IS NOT A REFUSAL. Meta answers eligibility with a bare
//    boolean and no reason, and an HTTP error means the question could not be
//    asked at all. On this estate that is the COMMON case: of five numbers, one
//    was eligible, none were ineligible, and three could not be asked because
//    the Meta Business AI terms had not been accepted for that WhatsApp Business
//    Account. A screen that renders those three as "not eligible" tells three
//    businesses Meta turned them down when nobody ever asked. So the terms case
//    is detected and rendered as the instruction it is.
//
// 2. ONBOARDING AND ENABLING ARE ONE ACT HERE, because they are two calls at
//    Meta and both succeed independently — the agent answers nobody until
//    rollout.enabled is set. Two buttons would let somebody leave it half on,
//    with no error anywhere and an agent that never replies.
//
// 3. TURNING THIS ON CHANGES WHAT THE REST OF THE PLATFORM DOES. While the agent
//    holds a conversation, our reply rules stop acting on it — deliberately,
//    because replying is how an app takes the thread back and two responders in
//    one chat is worse than none. That is a real consequence of pressing the
//    button and it is said on the button's own screen, not in documentation
//    nobody opens.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bot, Loader2, CheckCircle2, AlertTriangle, Info, Plus, Power, ExternalLink, RefreshCw,
} from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { whatsappService } from 'api/services/whatsapp.service';
import {
  whatsappAgentService, AgentEligibility, isTermsNotAccepted,
} from 'api/services/whatsappAgent.service';

interface NumberRow {
  phoneNumberId: string;
  phoneNumber?: string;
  displayName?: string;
  businessAccountId?: string;
  active?: boolean;
}

const WhatsAppAiAgent: React.FC = () => {
  const { currentOrganization } = useAuth();

  const [numbers, setNumbers] = useState<NumberRow[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [eligibility, setEligibility] = useState<AgentEligibility | null>(null);
  const [checking, setChecking] = useState(false);

  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // What it should know.
  const [businessName, setBusinessName] = useState('');
  const [hours, setHours] = useState('');
  const [locations, setLocations] = useState('');
  const [website, setWebsite] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [instructions, setInstructions] = useState('');
  const [added, setAdded] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = currentOrganization?.id
        ? await whatsappService.getAllConfigsForOrganization(currentOrganization.id)
        : await whatsappService.getAllConfigs();
      const rows: NumberRow[] = ((res as any)?.data || []).filter((c: NumberRow) => c.active !== false);
      setNumbers(rows);
      if (rows.length === 1) setSelected(rows[0].phoneNumberId);
      if (!(res as any)?.success && !rows.length) setError('Could not load your WhatsApp numbers.');
      setLoading(false);
    })();
  }, [currentOrganization?.id]);

  const check = useCallback(async (phoneNumberId: string) => {
    if (!phoneNumberId) return;
    setChecking(true);
    setEligibility(null);
    const res = await whatsappAgentService.eligibility(phoneNumberId);
    setEligibility(res.success && res.data ? res.data : {
      phoneNumberId, checked: false, eligible: null,
      reason: res.error || 'The check itself failed.',
    });
    setChecking(false);
  }, []);

  useEffect(() => { if (selected) check(selected); }, [selected, check]);

  const current = useMemo(
    () => numbers.find((n) => n.phoneNumberId === selected), [numbers, selected]);

  const run = async (label: string, fn: () => Promise<any>) => {
    setBusy(label);
    setNote(null);
    const res = await fn();
    setBusy(null);
    if (res?.success) {
      setNote(`${label} — saved.`);
      setAdded((a) => [label, ...a].slice(0, 8));
    } else {
      // Meta's own words reach the screen. "Accept the terms" and "this number
      // is not yours" are the same status code to anyone who shows a generic
      // message, and they need completely different actions.
      setNote(res?.error || `${label} — did not save.`);
    }
  };

  const ready = eligibility?.checked === true && eligibility.eligible === true;

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      <div className="flex items-center gap-2 mb-1">
        <Bot className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">WhatsApp AI agent</h1>
      </div>
      <p className="text-[12px] mb-5" style={{ color: 'var(--text-muted)' }}>
        Meta&rsquo;s own agent answers on your number — questions, bookings, qualifying a lead —
        and hands over to you when it should.
      </p>

      {loading && (
        <p className="text-[12px] inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-3 h-3 animate-spin" /> Loading your numbers…
        </p>
      )}
      {error && <p className="text-[12px] text-amber-700 dark:text-amber-300">{error}</p>}

      {!loading && numbers.length === 0 && (
        <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
          No connected WhatsApp number yet. Connect one on the Setup screen first.
        </p>
      )}

      {numbers.length > 0 && (
        <>
          <label className="block text-[11px] font-medium uppercase tracking-wide mb-1"
                 style={{ color: 'var(--text-muted)' }}>
            Number
          </label>
          <select
            className="tl-input w-full sm:w-96 mb-4"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Choose a number…</option>
            {numbers.map((n) => (
              <option key={n.phoneNumberId} value={n.phoneNumberId}>
                {n.phoneNumber || n.phoneNumberId}{n.displayName ? ` — ${n.displayName}` : ''}
              </option>
            ))}
          </select>
        </>
      )}

      {selected && (
        <section className="tl-card mb-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100">Can this number run an agent?</h2>
            <button type="button" onClick={() => check(selected)} disabled={checking}
                    className="text-[11px] inline-flex items-center gap-1 text-indigo-600
                               dark:text-indigo-400 hover:underline disabled:opacity-50">
              <RefreshCw className={`w-3 h-3 ${checking ? 'animate-spin' : ''}`} /> Check again
            </button>
          </div>

          {checking && (
            <p className="text-[12px] inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
              <Loader2 className="w-3 h-3 animate-spin" /> Asking Meta…
            </p>
          )}

          {/* The terms case, which is most of them. Rendered as the instruction
              it is rather than as a refusal — nobody has been refused here. */}
          {!checking && isTermsNotAccepted(eligibility) && (
            <div className="rounded-lg px-3 py-2.5 bg-amber-50 text-amber-900
                            dark:bg-amber-900/20 dark:text-amber-200">
              <p className="text-[12px] font-medium flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                One step first — and it is not a refusal
              </p>
              <p className="text-[12px] mt-1.5">
                Meta will not even answer whether this number qualifies until the
                <strong> Meta Business AI terms</strong> are accepted for the WhatsApp Business
                Account that owns it{current?.businessAccountId ? ` (${current.businessAccountId})` : ''}.
              </p>
              <p className="text-[12px] mt-1.5">
                Someone who is an <strong>admin of the business portfolio</strong> accepts them in
                WhatsApp Manager, under <strong>Phone numbers</strong> for that account. We cannot
                do this for you — Meta requires the account&rsquo;s own admin.
              </p>
              <a href="https://business.facebook.com/wa/manage/phone-numbers/"
                 target="_blank" rel="noreferrer"
                 className="text-[12px] mt-2 inline-flex items-center gap-1 underline underline-offset-2">
                <ExternalLink className="w-3 h-3" /> Open WhatsApp Manager
              </a>
              <p className="text-[11px] mt-2 opacity-80">
                Then press &ldquo;Check again&rdquo;.
              </p>
            </div>
          )}

          {/* Could not ask, for some other reason. Still not a refusal. */}
          {!checking && eligibility && !eligibility.checked && !isTermsNotAccepted(eligibility) && (
            <div className="rounded-lg px-3 py-2.5 bg-gray-50 dark:bg-gray-800/60">
              <p className="text-[12px] font-medium flex items-center gap-1.5 text-gray-800 dark:text-gray-100">
                <Info className="w-3.5 h-3.5 shrink-0" /> We could not ask Meta about this number
              </p>
              <p className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                This is not the same as being turned down — the question did not get through.
                Meta said:
              </p>
              <pre className="text-[11px] mt-1.5 whitespace-pre-wrap break-words"
                   style={{ color: 'var(--text-muted)' }}>{eligibility.reason}</pre>
            </div>
          )}

          {!checking && eligibility?.checked && eligibility.eligible === false && (
            <p className="text-[12px] text-gray-800 dark:text-gray-100">
              Meta says this number does not qualify. It does not say why — the usual reasons are
              the business category on the account, the country, or business verification.
            </p>
          )}

          {!checking && ready && (
            <p className="text-[12px] inline-flex items-center gap-1.5 text-green-700 dark:text-green-400">
              <CheckCircle2 className="w-3.5 h-3.5" /> This number can run an agent.
            </p>
          )}
        </section>
      )}

      {ready && (
        <>
          <section className="tl-card mb-4">
            <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              What should it know?
            </h2>
            <p className="text-[12px] mb-3" style={{ color: 'var(--text-muted)' }}>
              Each of these is saved on its own, and you can come back and add more later.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Business name" value={businessName} onChange={setBusinessName} />
              <Field label="Opening hours" value={hours} onChange={setHours} placeholder="Mon–Sat, 9am–7pm" />
            </div>
            <Field label="Locations (comma separated)" value={locations} onChange={setLocations}
                   placeholder="Koregaon Park, Pune" />
            <button type="button" className="tl-btn tl-btn-secondary tl-btn-sm mt-2"
                    disabled={busy !== null || (!businessName && !hours && !locations)}
                    onClick={() => run('Business details', () => whatsappAgentService.businessInfo(selected, {
                      businessName: businessName || undefined,
                      hours: hours || undefined,
                      locations: locations
                        ? locations.split(',').map((l) => l.trim()).filter(Boolean)
                        : undefined,
                    }))}>
              {busy === 'Business details' ? 'Saving…' : 'Save business details'}
            </button>

            <hr className="my-4 border-gray-100 dark:border-gray-700" />

            <Field label="Website to read" value={website} onChange={setWebsite}
                   placeholder="https://example.com" />
            <button type="button" className="tl-btn tl-btn-secondary tl-btn-sm mt-2"
                    disabled={busy !== null || !website.trim()}
                    onClick={() => run('Website', () => whatsappAgentService.website(selected, website.trim()))}>
              {busy === 'Website' ? 'Adding…' : 'Add website'}
            </button>

            <hr className="my-4 border-gray-100 dark:border-gray-700" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="A question customers ask" value={question} onChange={setQuestion}
                     placeholder="Do you have parking?" />
              <Field label="The answer" value={answer} onChange={setAnswer}
                     placeholder="Yes, free parking for guests." />
            </div>
            <button type="button" className="tl-btn tl-btn-secondary tl-btn-sm mt-2 inline-flex items-center gap-1"
                    disabled={busy !== null || !question.trim() || !answer.trim()}
                    onClick={async () => {
                      await run('Question', () =>
                        whatsappAgentService.faq(selected, question.trim(), answer.trim()));
                      setQuestion(''); setAnswer('');
                    }}>
              <Plus className="w-3 h-3" /> {busy === 'Question' ? 'Adding…' : 'Add question'}
            </button>
          </section>

          <section className="tl-card mb-4">
            <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              How should it sound?
            </h2>
            <p className="text-[12px] mb-2" style={{ color: 'var(--text-muted)' }}>
              Plain instructions, in your own words — tone, what to prioritise, what never to say.
            </p>
            <textarea
              className="tl-input w-full min-h-[90px]"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Be brief and warm. Always offer a callback if you cannot answer. Never quote prices."
            />
            <button type="button" className="tl-btn tl-btn-secondary tl-btn-sm mt-2"
                    disabled={busy !== null || !instructions.trim()}
                    onClick={() => run('Instructions', () =>
                      whatsappAgentService.skills(selected, instructions.trim()))}>
              {busy === 'Instructions' ? 'Saving…' : 'Save instructions'}
            </button>
          </section>

          <section className="tl-card">
            <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Switch it on</h2>

            {/* Said here, on the button's own screen. Turning this on changes
                what the rest of the platform does, and finding that out
                afterwards is how somebody concludes their automation broke. */}
            <div className="rounded-lg px-3 py-2.5 my-2 bg-gray-50 dark:bg-gray-800/60">
              <p className="text-[12px] font-medium text-gray-800 dark:text-gray-100">
                While the agent is answering:
              </p>
              <ul className="text-[12px] mt-1 space-y-1 list-disc pl-4" style={{ color: 'var(--text-muted)' }}>
                <li>Your <strong>reply rules stop acting</strong> on chats it holds. Messages are still
                    recorded and still appear in Chats — but the agent answers, not your rules.
                    Two responders in one conversation is worse than one.</li>
                <li>Campaigns, templates and the rest of the number are unaffected.</li>
                <li>You can take any single conversation back, and hand it to the agent again,
                    from Chats.</li>
              </ul>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" className="tl-btn tl-btn-primary tl-btn-sm inline-flex items-center gap-1"
                      disabled={busy !== null}
                      onClick={() => run('Agent', () => whatsappAgentService.enable(selected))}>
                <Power className="w-3 h-3" /> {busy === 'Agent' ? 'Switching on…' : 'Switch the agent on'}
              </button>
              <button type="button" className="tl-btn tl-btn-secondary tl-btn-sm"
                      disabled={busy !== null}
                      onClick={() => run('Agent off', () => whatsappAgentService.disable(selected))}>
                Switch it off
              </button>
            </div>
            <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
              Switching it off leaves everything you entered in place, so turning it back on does not
              mean typing it again.
            </p>
          </section>
        </>
      )}

      {note && (
        <p className="text-[12px] mt-4 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-800/60
                      text-gray-800 dark:text-gray-100 break-words">{note}</p>
      )}
      {added.length > 0 && (
        <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
          Saved this session: {added.join(', ')}
        </p>
      )}
    </div>
  );
};

const Field: React.FC<{
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => (
  <div className="mt-2">
    <label className="block text-[11px] font-medium uppercase tracking-wide mb-1"
           style={{ color: 'var(--text-muted)' }}>{label}</label>
    <input className="tl-input w-full" value={value} placeholder={placeholder}
           onChange={(e) => onChange(e.target.value)} />
  </div>
);

export default WhatsAppAiAgent;
